
import { Component, Vue } from 'vue-property-decorator';
import { ref } from 'vue';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteRepositoryInterface from '@/services/Website/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import CreateWebsiteForm from '@/services/Website/forms/CreateWebsiteForm';
import WebsiteGroupRepositoryInterface, { WebsiteGroup, WebsiteGroupItem } from '@/services/WebsiteGroup/interfaces';
import { UserInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { PlanInterface, PlanRepositoryInterface } from '@/services/Plan/interfaces';
import { SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  inject: [
    'websiteRepository',
    'serverRepository',
    'websiteGroupRepository',
    'userRepository',
    'planRepository',
  ],
})
export default class CreateWebsite extends Vue {
  public loading: boolean = true;

  public websiteRepository!: WebsiteRepositoryInterface;
  public serverRepository!: ServerRepositoryInterface;
  public websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  public userRepository!: UserRepoInterface;
  public planRepository!: PlanRepositoryInterface;

  public form: WPXFormValue<CreateWebsiteForm> = new WPXFormValue<CreateWebsiteForm>(CreateWebsiteForm);

  public servers: ServerInterface[] = [];
  public groups: WebsiteGroupItem[] = [];
  public users: UserInterface[] = [];
  public plans: PlanInterface[] = [];
  public planLocked: boolean = false;

  public excludeActions: string[] = ['email', 'wp'];

  public async created(): Promise<void> {
    try {
      await this.initialize();
      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.message || err.message);
    }
  }

  public async initialize(): Promise<void> {
    await this.getServers();
    await this.getUsers();
    await this.getPlans();
  }

  public async getServers(): Promise<void> {
    this.servers = await this.serverRepository.getAllServers();
  }

  public async loadServerGroups(): Promise<void> {
    if (!this.form.data.originServer) {
      return;
    }

    this.form.data.userId = '';
    this.form.data.groupId = null;
    this.groups = [];

    try {
      this.groups = await this.websiteGroupRepository.getServerGroups(this.form.data.originServer);
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
      this.groups = [];
    }
  }

  public groupText(group: WebsiteGroupItem): string {
    let text = group.id.toString();

    if (group.mainDomain) {
      text = `${text} <- ${group.mainDomain}`;
    }

    let plan = this.plans.find((p) => p.id === group.plan);
    if (!plan) {
      return text;
    }

    text = `${text} -> ${plan.name}`;

    return text;
  }

  public async loadUserGroups(userId: string): Promise<void> {
    if (!userId) {
      return;
    }

    this.groups = [];

    const groupsData = await this.websiteGroupRepository.getServerGroups(this.form.data.originServer, userId);

    this.groups = groupsData;
  }

  public async getUsers(): Promise<void> {
    [this.users] = await this.userRepository.getAllUsers();
  }

  public async getPlans(): Promise<void> {
    this.plans = await this.planRepository.getAll();
  }

  public async setUserId(groupId: number): Promise<void> {
    if (!groupId) {
      this.planLocked = false;
      this.form.data.planId = null;
      return;
    }


    const group = this.groups.find((g) => g.id === groupId);
    if (!group || !group.owner) {
      this.planLocked = false;
      this.form.data.planId = null;
      return;
    }


    this.users = this.users.filter((u) => u.id !== u.username);

    this.form.data.userId = group.owner;

    this.users.push({ id: group.owner, username: group.owner } as unknown as UserInterface);

    this.form.data.planId = group.plan ?? 1;

    this.planLocked = true;
  }

  public async createWebsite(): Promise<void> {
    try {
      await this.websiteRepository.createWebsite(
        this.form.data.originServer,
        this.form.data.domain,
        this.form?.data?.userId ? this.form.data.userId : this.$store.getters.id,
        this.form.data.groupId,
        this.excludeActions,
        this.form?.data?.planId || 1,
      );

      this.$successDialog.show('Website creation started!');
      await this.$router.push({ name: 'website_all' });
    } catch (err) {
      this.$errorDialog.show(err.response?.data?.error || err.message);
    }
  }

  public excludeAction(action: string, value: boolean): void {
    if (value) {
      this.excludeActions = this.excludeActions.filter((a) => a !== action);

      return;
    }

    this.excludeActions.push(action);
  }
}
